import { create } from '@bufbuild/protobuf'
import { createClient } from '@connectrpc/connect'
import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import { getListAlertsQueryKey } from '@/api/alerts.hook'
import { listSignalTypes } from '@/gen/inventory/v1/alert_rules_service-AlertRuleService_connectquery'
import { type SignalType, SignalTypeSchema } from '@/gen/inventory/v1/signal_pb'
import {
  addSignalComment,
  createOrgSignal,
  createSignal,
  getSignal,
  getSignalTimeline,
  listSignals,
  listSignalsIncludingInvisible,
  markSignalFalsePositive,
  setOrgSignalVisibility,
} from '@/gen/inventory/v1/signal_service-SignalService_connectquery'
import {
  GetSignalResponse,
  ListSignalsRequest,
  ListSignalsResponseSchema,
  SignalService,
} from '@/gen/inventory/v1/signal_service_pb'

import { useFeatureFlagEnabled } from '@/lib/config/featureflag'
import { defaultTransport } from '@/lib/connect/connect-transport'

const signalServiceClient = createClient(SignalService, defaultTransport)

export const getGetSignalTimelineQueryKey = (signalId?: string) => {
  return createConnectQueryKey({
    schema: SignalService.method.getSignalTimeline,
    input: { id: signalId ?? '' },
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

export const useGetSignalTimeline = (signalId?: string) => {
  const queryState = useQuery(getSignalTimeline, { id: signalId ?? '' }, { enabled: !!signalId })
  return {
    ...queryState,
    data: queryState.data?.items || [],
  }
}

export const getGetSignalQueryKey = (id?: string, staticOutput = false) => {
  return createConnectQueryKey({
    schema: SignalService.method.getSignal,
    input: { id, staticOutput },
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

export const useGetSignal = (id?: string, staticOutput = false) => {
  return useQuery(getSignal, { id: id ?? '', staticOutput }, { enabled: !!id })
}

export const getListSignalsQueryKey = () => {
  return createConnectQueryKey({
    schema: SignalService.method.listSignals,
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

const NO_SIGNAL_TYPE_TITLES: ReadonlyMap<SignalType, string> = new Map()

const SIGNAL_TYPE_BY_ID = new Map(
  SignalTypeSchema.values.map((v): [string, SignalType] => [v.name, v.number]),
)

// Titles of the org's rule catalog types; types customers never see a type for are absent.
export const useSignalTypeTitles = (): ReadonlyMap<SignalType, string> => {
  const { data } = useQuery(listSignalTypes, {}, { staleTime: Infinity })
  return useMemo(
    () =>
      data
        ? new Map(
            data.signalTypes.flatMap((t): [SignalType, string][] => {
              const signalType = SIGNAL_TYPE_BY_ID.get(t.id)
              return signalType === undefined ? [] : [[signalType, t.displayName]]
            }),
          )
        : NO_SIGNAL_TYPE_TITLES,
    [data],
  )
}

// FF on → support RPC (includes hidden, "* "-prefixed titles from BE).
export const useListSignals = (req?: ListSignalsRequest) => {
  const { isEnabled } = useFeatureFlagEnabled('signal-show-even-if-invisible')

  const reqBody = { companyId: req?.companyId }
  const visibleOnlyRes = useQuery(listSignals, reqBody, { enabled: !isEnabled })
  const includeHiddenRes = useQuery(listSignalsIncludingInvisible, reqBody, { enabled: isEnabled })
  const res = isEnabled ? includeHiddenRes : visibleOnlyRes

  const data = create(ListSignalsResponseSchema, {
    signals: res.data?.signals ?? [],
  })

  return {
    ...res,
    data,
  }
}

// No batch RPC; fan out getSignal. BE filters visibility + stamps "* ".
export const useGetSignals = (ids: string[]) => {
  const client = createClient(SignalService, defaultTransport)
  const validIds = ids.filter((id) => id)

  const queryResults = useQueries({
    queries: validIds.map((id) => ({
      queryKey: getGetSignalQueryKey(id),
      queryFn: () => client.getSignal({ id }),
      enabled: validIds.length > 0,
    })),
  })

  const data = useMemo(
    () =>
      queryResults.filter((result) => result.data?.signal).map((result) => result.data!.signal!),
    [queryResults],
  )

  return {
    isPending: queryResults.some((result) => result.isPending),
    isError: queryResults.some((result) => result.isError),
    data,
  }
}

export const fetchSignalsWithMarkdown = async (ids: string[], staticOutput = false) => {
  const validIds = ids.filter((id) => id)
  if (validIds.length === 0) {
    return []
  }

  const promises = validIds.map((id) => signalServiceClient.getSignal({ id, staticOutput }))
  const results = await Promise.allSettled(promises)

  return results
    .filter(
      (result): result is PromiseFulfilledResult<GetSignalResponse> =>
        result.status === 'fulfilled',
    )
    .map((result) => result.value)
}

export const useGetSignalsWithMarkdown = ({
  ids,
  staticOutput = false,
  enabled = true,
}: {
  ids: string[]
  staticOutput?: boolean
  enabled?: boolean
}) => {
  const client = createClient(SignalService, defaultTransport)
  const validIds = useMemo(() => ids.filter((id) => id), [ids])

  const queries = useQueries({
    queries: validIds.map((id) => ({
      queryKey: getGetSignalQueryKey(id, staticOutput),
      queryFn: () => client.getSignal({ id, staticOutput }),
      enabled: enabled && !!id,
    })),
  })

  return {
    isPending: queries.some((query) => query.isPending),
    isError: queries.some((query) => query.isError),
    data: queries.map((query) => query.data as GetSignalResponse),
    queries,
  }
}

export const useCreateOrgSignal = () => {
  return useMutation(createOrgSignal)
}

export const useCreateSignal = () => {
  return useMutation(createSignal)
}

export const useMarkSignalFalsePositive = (signalId: string) => {
  const queryClient = useQueryClient()
  return useMutation(markSignalFalsePositive, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getGetSignalQueryKey(signalId) })
      queryClient.invalidateQueries({ queryKey: getGetSignalTimelineQueryKey(signalId) })
      queryClient.invalidateQueries({ queryKey: getListAlertsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListSignalsQueryKey() })
    },
  })
}

export const useAddSignalComment = (signalId: string) => {
  const queryClient = useQueryClient()
  return useMutation(addSignalComment, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getGetSignalTimelineQueryKey(signalId) })
    },
  })
}

export const useSetOrgSignalVisibility = (signalId: string) => {
  const queryClient = useQueryClient()
  return useMutation(setOrgSignalVisibility, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getGetSignalQueryKey(signalId) })
      queryClient.invalidateQueries({ queryKey: getListAlertsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListSignalsQueryKey() })
    },
  })
}
